/**
 * WebMCP: udstiller butikkens vigtigste handlinger som tools til AI-agenter
 * i browseren via navigator.modelContext (Chrome early preview / WebMCP-spec).
 *
 * Alle tools kalder kun Shopifys egne same-origin storefront-endpoints
 * (/search/suggest.json, /products/<handle>.js, /cart*.js, /collections/<handle>/products.json).
 * Ingen betaling: start_checkout sender kun kunden til Shopifys checkout.
 *
 * Konfiguration (ruter, valuta, sidekontekst) kommer fra snippets/webmcp.liquid
 * i <script type="application/json" id="webmcp-config">.
 *
 * Se docs/WEBMCP.md.
 */
(function () {
  'use strict';

  var modelContext = typeof navigator !== 'undefined' ? navigator.modelContext : null;
  if (!modelContext) return;

  var config = readConfig();
  var routes = config.routes || {};
  var rootUrl = routes.root_url || '/';
  var currency = config.currency || 'DKK';

  function readConfig() {
    var el = document.getElementById('webmcp-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function withRoot(path) {
    if (rootUrl === '/' || rootUrl === '') return path;
    var base = rootUrl.replace(/\/$/, '');
    return path.indexOf(base + '/') === 0 ? path : base + path;
  }

  function absolute(path) {
    if (!path) return null;
    if (/^https?:\/\//.test(path)) return path;
    return location.origin + path;
  }

  function money(cents) {
    if (typeof cents !== 'number') return null;
    return { amount: Math.round(cents) / 100, currency: currency };
  }

  function stripHtml(html) {
    if (!html) return '';
    var div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function truncate(text, max) {
    if (!text || text.length <= max) return text;
    return text.slice(0, max - 1) + '…';
  }

  function ok(data) {
    return { content: [{ type: 'text', text: JSON.stringify(data) }] };
  }

  function fail(message) {
    return { content: [{ type: 'text', text: 'Error: ' + message }], isError: true };
  }

  function request(url, options) {
    var opts = Object.assign({ credentials: 'same-origin' }, options || {});
    opts.headers = Object.assign(
      { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      opts.headers || {}
    );
    return fetch(url, opts).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (body) {
          if (!res.ok) {
            var msg = body && (body.description || body.message) ? body.description || body.message : 'HTTP ' + res.status;
            throw new Error(msg);
          }
          return body;
        });
    });
  }

  function postJson(url, payload) {
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function refreshCartUi() {
    try {
      if (typeof window.dispatchCustomEvent === 'function') {
        window.dispatchCustomEvent('cart:refresh');
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) {
      /* UI-opdatering er best effort */
    }
  }

  function clampInt(value, min, max, fallback) {
    var n = parseInt(value, 10);
    if (isNaN(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  }

  /* ---------- mapping af Shopify-svar til kompakte objekter ---------- */

  function mapSuggestProduct(p) {
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: absolute(p.url),
      vendor: p.vendor,
      product_type: p.type,
      price: p.price != null ? { amount: Number(p.price), currency: currency } : null,
      compare_at_price: p.compare_at_price_max ? { amount: Number(p.compare_at_price_max), currency: currency } : null,
      available: p.available,
      image: p.featured_image && p.featured_image.url ? p.featured_image.url : p.image || null
    };
  }

  function mapProductJs(p) {
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: absolute(withRoot('/products/' + p.handle)),
      vendor: p.vendor,
      product_type: p.type,
      tags: p.tags,
      available: p.available,
      price: money(p.price),
      price_min: money(p.price_min),
      price_max: money(p.price_max),
      compare_at_price: p.compare_at_price ? money(p.compare_at_price) : null,
      description: truncate(stripHtml(p.description), 2000),
      options: (p.options || []).map(function (o) {
        return typeof o === 'string' ? o : { name: o.name, values: o.values };
      }),
      variants: (p.variants || []).map(function (v) {
        return {
          variant_id: v.id,
          title: v.title,
          sku: v.sku,
          options: v.options,
          price: money(v.price),
          compare_at_price: v.compare_at_price ? money(v.compare_at_price) : null,
          available: v.available
        };
      }),
      images: (p.images || []).slice(0, 5)
    };
  }

  function mapCollectionProduct(p) {
    var variants = p.variants || [];
    var prices = variants.map(function (v) {
      return Number(v.price);
    });
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: absolute(withRoot('/products/' + p.handle)),
      vendor: p.vendor,
      product_type: p.product_type,
      price_min: prices.length ? { amount: Math.min.apply(null, prices), currency: currency } : null,
      price_max: prices.length ? { amount: Math.max.apply(null, prices), currency: currency } : null,
      available: variants.some(function (v) {
        return v.available;
      }),
      variants: variants.map(function (v) {
        return { variant_id: v.id, title: v.title, sku: v.sku, price: { amount: Number(v.price), currency: currency }, available: v.available };
      }),
      image: p.images && p.images[0] ? p.images[0].src : null
    };
  }

  function mapCart(cart) {
    return {
      item_count: cart.item_count,
      total_price: money(cart.total_price),
      currency: cart.currency || currency,
      cart_url: absolute(routes.cart_url || withRoot('/cart')),
      checkout_url: absolute(withRoot('/checkout')),
      items: (cart.items || []).map(function (item) {
        return {
          line_item_key: item.key,
          variant_id: item.variant_id,
          product_id: item.product_id,
          product_title: item.product_title,
          variant_title: item.variant_title,
          sku: item.sku,
          quantity: item.quantity,
          price: money(item.price),
          line_price: money(item.final_line_price != null ? item.final_line_price : item.line_price),
          url: absolute(item.url),
          image: item.image || null
        };
      })
    };
  }

  /* ---------- tools ---------- */

  var tools = [
    {
      name: 'search_products',
      description:
        'Search the Pedalatleten.dk catalogue (bikes, e-bikes, parts, accessories and clothing) by free-text query. Returns matching products with price, availability and product page URL. Use get_product with the returned handle for full details and variants.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search words, e.g. brand, model, category or SKU.' },
          limit: { type: 'integer', minimum: 1, maximum: 10, default: 8, description: 'Maximum number of products to return (1-10).' }
        },
        required: ['query']
      },
      execute: function (input) {
        input = input || {};
        var query = String(input.query || '').trim();
        if (!query) return fail('query is required');
        var limit = clampInt(input.limit, 1, 10, 8);
        var url =
          (routes.predictive_search_url || withRoot('/search/suggest')) +
          '.json?q=' +
          encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=' +
          limit +
          '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag,variants.sku';
        return request(url.replace(/\.json\.json/, '.json'))
          .then(function (body) {
            var products = (body.resources && body.resources.results && body.resources.results.products) || [];
            return ok({
              query: query,
              count: products.length,
              products: products.map(mapSuggestProduct),
              search_page_url: absolute((routes.search_url || withRoot('/search')) + '?q=' + encodeURIComponent(query))
            });
          })
          .catch(function (e) {
            return fail('search failed: ' + e.message);
          });
      }
    },
    {
      name: 'get_product',
      description:
        'Get full details for one product by its handle (the last part of the product URL): description, options, all variants with variant_id, price, SKU and stock status, and images. Use the variant_id with add_to_cart.',
      inputSchema: {
        type: 'object',
        properties: {
          handle: { type: 'string', description: 'Product handle, e.g. "trek-fx-3-disc" from /products/trek-fx-3-disc.' }
        },
        required: ['handle']
      },
      execute: function (input) {
        input = input || {};
        var handle = String(input.handle || '')
          .trim()
          .replace(/^.*\/products\//, '')
          .replace(/[?#].*$/, '')
          .replace(/\/$/, '');
        if (!handle) return fail('handle is required');
        return request(withRoot('/products/' + encodeURIComponent(handle) + '.js'))
          .then(function (p) {
            return ok(mapProductJs(p));
          })
          .catch(function (e) {
            return fail('product "' + handle + '" not found: ' + e.message);
          });
      }
    },
    {
      name: 'list_collection_products',
      description:
        'List products in a collection (category) by collection handle, e.g. "elcykler", "racercykler" or "mountainbikes". Paginated. Returns price range, availability and variant ids.',
      inputSchema: {
        type: 'object',
        properties: {
          collection_handle: { type: 'string', description: 'Collection handle from /collections/<handle>.' },
          page: { type: 'integer', minimum: 1, default: 1, description: 'Page number, starting at 1.' },
          limit: { type: 'integer', minimum: 1, maximum: 50, default: 20, description: 'Products per page (1-50).' }
        },
        required: ['collection_handle']
      },
      execute: function (input) {
        input = input || {};
        var handle = String(input.collection_handle || '')
          .trim()
          .replace(/^.*\/collections\//, '')
          .replace(/[?#].*$/, '')
          .replace(/\/.*$/, '');
        if (!handle) return fail('collection_handle is required');
        var page = clampInt(input.page, 1, 1000, 1);
        var limit = clampInt(input.limit, 1, 50, 20);
        var url = withRoot('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit + '&page=' + page);
        return request(url)
          .then(function (body) {
            var products = body.products || [];
            return ok({
              collection_handle: handle,
              collection_url: absolute(withRoot('/collections/' + handle)),
              page: page,
              count: products.length,
              has_more: products.length === limit,
              products: products.map(mapCollectionProduct)
            });
          })
          .catch(function (e) {
            return fail('collection "' + handle + '" not found: ' + e.message);
          });
      }
    },
    {
      name: 'get_cart',
      description: 'Get the current shopping cart: line items with line_item_key, variant_id, quantity and prices, plus totals and the checkout URL.',
      inputSchema: { type: 'object', properties: {} },
      execute: function () {
        return request(withRoot('/cart.js'))
          .then(function (cart) {
            return ok(mapCart(cart));
          })
          .catch(function (e) {
            return fail('could not read cart: ' + e.message);
          });
      }
    },
    {
      name: 'add_to_cart',
      description:
        'Add a product variant to the shopping cart. Requires a numeric variant_id (from get_product, search results or list_collection_products). Returns the updated cart. Nothing is purchased; the customer completes payment in checkout.',
      inputSchema: {
        type: 'object',
        properties: {
          variant_id: { type: 'integer', description: 'Shopify variant id.' },
          quantity: { type: 'integer', minimum: 1, default: 1, description: 'Quantity to add.' }
        },
        required: ['variant_id']
      },
      execute: function (input) {
        input = input || {};
        var variantId = parseInt(String(input.variant_id).replace(/^gid:\/\/shopify\/ProductVariant\//, ''), 10);
        if (!variantId) return fail('variant_id must be a numeric Shopify variant id');
        var quantity = clampInt(input.quantity, 1, 99, 1);
        return postJson(routes.cart_add_url || withRoot('/cart/add.js'), { items: [{ id: variantId, quantity: quantity }] })
          .then(function () {
            refreshCartUi();
            return request(withRoot('/cart.js'));
          })
          .then(function (cart) {
            return ok({ added: { variant_id: variantId, quantity: quantity }, cart: mapCart(cart) });
          })
          .catch(function (e) {
            return fail('could not add to cart: ' + e.message);
          });
      }
    },
    {
      name: 'update_cart_item',
      description:
        'Change the quantity of a cart line, identified by line_item_key or variant_id from get_cart. Set quantity to 0 to remove the line. Returns the updated cart.',
      inputSchema: {
        type: 'object',
        properties: {
          line_item_key: { type: 'string', description: 'Line item key from get_cart (preferred).' },
          variant_id: { type: 'integer', description: 'Variant id, used if line_item_key is not given.' },
          quantity: { type: 'integer', minimum: 0, description: 'New quantity. 0 removes the line.' }
        },
        required: ['quantity']
      },
      execute: function (input) {
        input = input || {};
        var id = input.line_item_key || input.variant_id;
        if (!id) return fail('line_item_key or variant_id is required');
        var quantity = clampInt(input.quantity, 0, 99, null);
        if (quantity === null) return fail('quantity is required');
        return postJson(routes.cart_change_url || withRoot('/cart/change.js'), { id: id, quantity: quantity })
          .then(function (cart) {
            refreshCartUi();
            return ok({ updated: { id: id, quantity: quantity }, cart: mapCart(cart) });
          })
          .catch(function (e) {
            return fail('could not update cart: ' + e.message);
          });
      }
    },
    {
      name: 'get_page_context',
      description:
        'Describe the page the user is currently viewing: page type, URL, and the product or collection handle if on a product or collection page. Call this first to understand what the user is looking at.',
      inputSchema: { type: 'object', properties: {} },
      execute: function () {
        var page = config.page || {};
        return ok({
          shop: config.shop || 'Pedalatleten',
          locale: config.locale || 'da',
          currency: currency,
          url: location.href,
          title: document.title,
          template: page.template || null,
          product_handle: page.product_handle || null,
          product_title: page.product_title || null,
          collection_handle: page.collection_handle || null,
          collection_title: page.collection_title || null,
          customer_logged_in: !!page.customer_logged_in
        });
      }
    },
    {
      name: 'open_page',
      description:
        'Navigate the browser to a page on this shop. Accepts a path like /products/<handle>, /collections/<handle>, /cart or /pages/contact, or a full pedalatleten.dk URL. Other domains are rejected.',
      inputSchema: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Path or same-origin URL to open.' }
        },
        required: ['path']
      },
      execute: function (input) {
        input = input || {};
        var raw = String(input.path || '').trim();
        if (!raw) return fail('path is required');
        var target;
        try {
          target = new URL(raw, location.origin);
        } catch (e) {
          return fail('invalid path');
        }
        if (target.origin !== location.origin || !/^https?:$/.test(target.protocol)) {
          return fail('only pages on ' + location.origin + ' can be opened');
        }
        location.assign(target.href);
        return ok({ navigating_to: target.href });
      }
    },
    {
      name: 'start_checkout',
      description:
        'Send the customer to Shopify checkout with the current cart. The customer enters delivery details and pays there; this tool never takes payment details.',
      inputSchema: { type: 'object', properties: {} },
      execute: function () {
        return request(withRoot('/cart.js'))
          .then(function (cart) {
            if (!cart.item_count) return fail('cart is empty, add a product first');
            var url = absolute(withRoot('/checkout'));
            location.assign(url);
            return ok({ navigating_to: url, item_count: cart.item_count, total_price: money(cart.total_price) });
          })
          .catch(function (e) {
            return fail('could not start checkout: ' + e.message);
          });
      }
    }
  ];

  /* ---------- registrering ---------- */

  function register() {
    try {
      if (typeof modelContext.provideContext === 'function') {
        modelContext.provideContext({ tools: tools });
      } else if (typeof modelContext.registerTool === 'function') {
        tools.forEach(function (tool) {
          modelContext.registerTool(tool);
        });
      } else {
        return false;
      }
      return true;
    } catch (e) {
      if (window.console && console.warn) console.warn('[webmcp] registration failed', e);
      return false;
    }
  }

  var registered = register();

  window.PedalatletenWebMCP = {
    version: '1.0.0',
    registered: registered,
    tools: tools,
    register: register
  };
})();
